import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Barcode, ArrowRight, Sparkles, FileSpreadsheet, Wand2, Send, ShieldCheck,
  Zap, Store, Rocket, CheckCircle2, Clock, TrendingUp, MessageCircle, ImageIcon,
} from "lucide-react";

const SCREENSHOT_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_scan-to-spreadsheet-3/artifacts/a3zgwkut_image.png"; // Batch table
const SCREENSHOT_PANEL = "https://customer-assets-0z36b82j.emergentagent.net/job_scan-to-spreadsheet-3/artifacts/92xk1856_image.png"; // Passo 2 review
const SCREENSHOT_BLING = "https://customer-assets-0z36b82j.emergentagent.net/job_scan-to-spreadsheet-3/artifacts/s2m02cqw_image.png"; // Bling produtos
const WHATSAPP_URL = "https://w.app/carlos_soglia";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[#09090B]">
      {/* ---------- Header ---------- */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white/95 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 font-display font-bold text-lg"
            data-testid="landing-logo"
          >
            <div className="w-8 h-8 rounded-md brand-primary flex items-center justify-center">
              <Barcode className="w-4 h-4 text-white" />
            </div>
            <span>MAGIC SIP</span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4">
            <a href="#como-funciona" className="hidden sm:inline text-sm text-zinc-700 hover:text-[#002FA7]">
              Como funciona
            </a>
            <a href="#recursos" className="hidden sm:inline text-sm text-zinc-700 hover:text-[#002FA7]">
              Recursos
            </a>
            <Link to="/pricing" className="hidden sm:inline text-sm text-zinc-700 hover:text-[#002FA7]">
              Planos
            </Link>
            <Link to="/login">
              <Button variant="outline" className="rounded-md border-zinc-300 h-9" data-testid="landing-login-btn">
                Entrar
              </Button>
            </Link>
            <Link to="/register">
              <Button className="brand-primary text-white rounded-md h-9" data-testid="landing-register-btn">
                Testar grátis <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#002FA7]" /> Utilitário para E-commerce
            </div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-6">
              Cadastre <span className="brand-text">até 10.000 produtos</span> no seu e-commerce em <span className="brand-text">minutos</span>, ao invés de perder dias protelando para publicar seus produtos e deixar de entrar nesse mercado que é o que mais cresce no mundo, o <span className="brand-text">mercado Online</span>.
            </h1>
            <p className="text-base text-zinc-600 leading-relaxed mb-4 max-w-xl">
              Envie uma planilha com até 10.000 produtos apenas com <strong>nome, categoria e preço</strong>.
              A IA faz o resto — busca fotos em catálogos brasileiros, gera descrição SEO, indicação
              de uso e SKU, e publica direto no seu <strong>Nuvemshop</strong> ou <strong>Bling</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/register">
                <Button className="brand-primary text-white rounded-md h-11 px-6" data-testid="landing-hero-cta">
                  <Zap className="w-4 h-4 mr-2" /> Começar 7 dias grátis
                </Button>
              </Link>
              <a href="#como-funciona">
                <Button variant="outline" className="rounded-md border-zinc-300 h-11 px-6" data-testid="landing-hero-secondary">
                  Ver como funciona
                </Button>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs font-mono text-zinc-500 flex-wrap">
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Sem cobrança no trial</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Cancela quando quiser</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Suporte via WhatsApp</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#002FA7]/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl" />
            <div className="relative border border-zinc-200 rounded-md shadow-xl overflow-hidden bg-black aspect-video">
              <iframe
                src="https://www.youtube.com/embed/xrJtAzJ9QBk?rel=0&modestbranding=1"
                title="Apresentação Magic SIP"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="landing-hero-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Metrics band ---------- */}
      <section className="border-y border-zinc-200 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { v: "10.000", l: "produtos por lote" },
            { v: "24 h", l: "para catálogo completo" },
            { v: "12+", l: "catálogos brasileiros" },
            { v: "2 clique", l: "publica em Nuvemshop/Bling" },
          ].map((m) => (
            <div key={m.l}>
              <div className="font-display text-2xl sm:text-3xl font-bold brand-text">{m.v}</div>
              <div className="text-xs font-mono text-zinc-500 uppercase mt-1">{m.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Como funciona ---------- */}
      <section id="como-funciona" className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="eyebrow mb-3">Fluxo em 3 passos</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Como funciona</h2>
          <p className="text-zinc-600 text-base">
            Você sobe uma planilha simples e o Magic SIP cuida da parte chata do cadastro em massa.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {[
            { icon: FileSpreadsheet, title: "1. Envie a planilha", desc: "Suporta CSV ou XLSX. Basta preencher Nome, Categoria e Preço. Opcionalmente, SKU, estoque, dimensões e peso.", testid: "step-upload" },
            { icon: Wand2, title: "2. A IA faz o trabalho", desc: "Busca imagens em catálogos como Drogaria SP, Kabum, Leroy Merlin. Filtra por Vision. Gera descrição SEO, SKU e preço sugerido.", testid: "step-ai" },
            { icon: Send, title: "3. Publica em 1 clique", desc: "Revise a tabela, escolha o ERP (Nuvemshop, Bling, Tiny, Omie) e publique. Com botão de rollback se algo der errado.", testid: "step-publish" },
          ].map((s) => (
            <div
              key={s.title}
              className="border border-zinc-200 rounded-md p-6 hover:border-[#002FA7] transition-colors"
              data-testid={s.testid}
            >
              <div className="w-11 h-11 rounded-md brand-primary text-white flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-display font-semibold text-lg mb-1">{s.title}</div>
              <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="border border-zinc-200 rounded-md overflow-hidden shadow-lg bg-zinc-50 max-w-4xl mx-auto">
          <img
            src={SCREENSHOT_URL}
            alt="Tabela de revisão do Magic SIP mostrando produtos processados"
            className="w-full block"
          />
        </div>
      </section>

      {/* ---------- Recursos ---------- */}
      <section id="recursos" className="bg-[#F8F9FA] border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="eyebrow mb-3">Recursos</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Feito para vender mais rápido</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Store, title: "Multi-ERP", desc: "Publique no Nuvemshop, Bling (OAuth 1-clique), Tiny ou Omie. Detecção de duplicatas por SKU/nome." },
              { icon: ImageIcon, title: "Imagens automáticas", desc: "Busca em 12+ catálogos VTEX (farmácia, pet, eletrônicos, alimentos, construção). Filtro Vision Gemini remove imagens erradas." },
              { icon: Sparkles, title: "IA para SEO e descrição", desc: "Descrição HTML, título SEO (60c), meta-description (160c), indicação de uso, preço sugerido, SKU — tudo automático." },
              { icon: Rocket, title: "Batch até 1000 itens", desc: "Persistência em segundo plano (Zustand + localStorage). Feche a aba, volte depois — nada é perdido." },
              { icon: ShieldCheck, title: "LGPD + Segurança", desc: "Credenciais criptografadas com Fernet. Webhooks LGPD do Nuvemshop implementados. Nunca guardamos dados de clientes finais." },
              { icon: TrendingUp, title: "Rollback e Undo", desc: "Botão 'Apagar publicados' remove um lote inteiro do ERP se algo sair errado. Delete real ou soft-delete no Bling automático." },
            ].map((f) => (
              <div key={f.title} className="border border-zinc-200 bg-white rounded-md p-5">
                <div className="w-9 h-9 rounded-md brand-primary text-white flex items-center justify-center mb-3">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="font-display font-semibold text-base mb-1">{f.title}</div>
                <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ERP integration showcase ---------- */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-3 flex items-center gap-2">
              <Store className="w-4 h-4 text-[#002FA7]" /> Integração nativa
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
              Produtos completos no seu Bling e Nuvemshop
            </h2>
            <p className="text-zinc-600 text-base mb-6 leading-relaxed">
              Cada produto sobe com <strong>imagem, descrição HTML, SEO, SKU, estoque e dimensões</strong> —
              pronto para o marketplace já mostrar. Se um item precisar de ajuste, você edita direto
              na tabela antes de publicar.
            </p>
            <ul className="space-y-2 text-sm text-zinc-700 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                Bling v3 com OAuth 1-clique + refresh token automático
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                Nuvemshop OAuth homologado + LGPD webhooks
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                Tiny e Omie via token/API keys
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                Detecção de duplicatas (últimos 60 dias)
              </li>
            </ul>
          </div>
          <div className="border border-zinc-200 rounded-md overflow-hidden shadow-lg bg-zinc-50">
            <img
              src={SCREENSHOT_BLING}
              alt="Painel do Bling mostrando produtos cadastrados via Magic SIP"
              className="w-full block"
            />
          </div>
        </div>
      </section>

      {/* ---------- CTA final ---------- */}
      <section className="brand-primary text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-md bg-white/10 flex items-center justify-center backdrop-blur">
              <Rocket className="w-6 h-6" />
            </div>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">
            Pare de perder tempo cadastrando produto por produto
          </h2>
          <p className="text-white/85 text-base mb-8 max-w-2xl mx-auto">
            7 dias grátis · sem cartão para começar · cancele quando quiser
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register">
              <Button className="bg-white text-[#002FA7] hover:bg-zinc-100 rounded-md h-11 px-8 font-semibold" data-testid="landing-final-cta">
                <Zap className="w-4 h-4 mr-2" /> Começar agora
              </Button>
            </Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-md h-11 px-6 border-white/40 bg-transparent text-white hover:bg-white/10">
                <MessageCircle className="w-4 h-4 mr-2" /> Falar com o Suporte
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 flex flex-col sm:flex-row justify-between gap-4 text-xs font-mono text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md brand-primary flex items-center justify-center">
              <Barcode className="w-3 h-3 text-white" />
            </div>
            <span>MAGIC SIP · v1.0 · UTILITÁRIO PARA E-COMMERCE</span>
          </div>
          <div className="flex gap-5 flex-wrap">
            <Link to="/pricing" className="hover:text-[#002FA7]">Planos</Link>
            <Link to="/privacy" className="hover:text-[#002FA7]">Política de Privacidade</Link>
            <Link to="/support" className="hover:text-[#002FA7]">Suporte</Link>
            <Link to="/login" className="hover:text-[#002FA7]">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
